import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ArticleSummary from '../components/DailyNews/ArticleSummary';
import DaySection from '../components/DailyNews/DaySection';
import Loading from '../components/Common/Loading';
import { PageErrorBoundary } from '../components/Common/ErrorBoundary';
import { Article } from '../config/api';
import { API_CONFIG } from '../config/api';
import { DailySummaryData } from '../components/DailyNews/DailySummary';
import './Home.css';

const ArticleDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [dailySummary, setDailySummary] = useState<DailySummaryData | null>(null);
  const [dayArticles, setDayArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticleAndDayData = async () => {
      if (!slug) {
        console.log('No slug provided');
        return;
      }
      
      console.log('Fetching article with slug:', slug);
      
      try {
        setLoading(true);
        
        // Fetch the main article
        const articleUrl = `${API_CONFIG.BASE_URL}/api/articles/slug/${slug}`;
        console.log('Making request to:', articleUrl);
        
        const articleResponse = await fetch(articleUrl);
        console.log('Article response status:', articleResponse.status);
        
        if (!articleResponse.ok) {
          throw new Error(`Article not found (${articleResponse.status})`);
        }
        
        const articleData = await articleResponse.json();
        console.log('Article data received:', articleData);
        const fetchedArticle = articleData.data;
        setArticle(fetchedArticle);

        // Fetch daily summary for this article's daily_summary_id
        if (fetchedArticle.daily_summary_id) {
          console.log('Fetching daily summary for ID:', fetchedArticle.daily_summary_id);
          const dailySummaryResponse = await fetch(`${API_CONFIG.BASE_URL}/api/daily-summaries/id/${fetchedArticle.daily_summary_id}`);
          console.log('Daily summary response status:', dailySummaryResponse.status);
          
          if (dailySummaryResponse.ok) {
            const dailySummaryData = await dailySummaryResponse.json();
            console.log('Daily summary data:', dailySummaryData);
            setDailySummary(dailySummaryData.data.dailySummary);
          } else {
            console.error('Failed to fetch daily summary:', dailySummaryResponse.status);
          }

          // Fetch all articles for this daily summary, excluding the current article
          const articlesResponse = await fetch(`${API_CONFIG.BASE_URL}/api/articles?dailySummaryIds=${fetchedArticle.daily_summary_id}&limit=1000`);
          if (articlesResponse.ok) {
            const articlesData = await articlesResponse.json();
            console.log('Day articles data:', articlesData);
            // Filter out the current article to avoid duplication
            const filteredArticles = (articlesData.data || []).filter(
              (art: Article) => art.id !== fetchedArticle.id
            );
            console.log('Filtered day articles:', filteredArticles);
            setDayArticles(filteredArticles);
          }
        } else {
          console.log('No daily_summary_id found for article');
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Article not found. Please check the URL or try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticleAndDayData();
  }, [slug]);

  if (loading) return <Loading />;
  if (error) return <div className="error-container"><h2>Error Loading Article</h2><p>{error}</p></div>;
  if (!article) return <div className="error-container"><h2>Article Not Found</h2></div>;

  return (
    <PageErrorBoundary>
      <div className="home-container">
        <main className="home-content">
          <div className="main-content">
            {/* Featured Article */}
            <article className="article-detail">
              <ArticleSummary 
                article={article}
                showSource={true}
                showTags={true}
                showImage={true}
                showShareButtons={true}
                isHighlighted={false}
                className="article-detail__summary"
                shareUrl={window.location.href}
              />
            </article>

            {/* Daily Summary and Other Articles from Same Day */}
            {dailySummary && (
              <section className="home-section">
                <DaySection 
                  dailySummary={dailySummary}
                  articles={dayArticles}
                  defaultExpanded={true}
                  showThemes={true}
                  showStats={true}
                />
              </section>
            )}
          </div>
        </main>
      </div>
    </PageErrorBoundary>
  );
};

export default ArticleDetail;
